import { useEffect, useRef, useState } from 'react';
import { tenantApi, type NotificationEmailHistoryItem } from '../../services/tenantApi';
import { formatDateTime } from '../../shared/dateTimeFormatting';

export function NotificationEmailHistory({ notificationId, reloadToken }: { notificationId: string; reloadToken: number }) {
  const [items, setItems] = useState<NotificationEmailHistoryItem[]>([]);
  const [nextSkip, setNextSkip] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const sequence = useRef(0);
  const controller = useRef<AbortController | null>(null);

  const load = async (skip: number) => {
    controller.current?.abort();
    const abort = new AbortController();
    controller.current = abort;
    const request = ++sequence.current;
    setLoading(true);
    setError('');
    try {
      const page = await tenantApi.getNotificationEmailHistory(notificationId, skip, abort.signal);
      if (request !== sequence.current) return;
      setItems(previous => skip === 0 ? page.items : [...previous, ...page.items]);
      setNextSkip(page.nextSkip);
    } catch (reason) {
      if (request === sequence.current && !abort.signal.aborted)
        setError(reason instanceof Error ? reason.message : 'Failed to load email submission history.');
    } finally {
      if (request === sequence.current) setLoading(false);
    }
  };

  useEffect(() => {
    setItems([]);
    setNextSkip(null);
    void load(0);
    return () => { sequence.current++; controller.current?.abort(); };
  }, [notificationId, reloadToken, refresh]);

  return <section className="notification-email-history" aria-label="Email submission history" aria-busy={loading}>
    <div className="notification-event-heading">
      <h4>Email submission history</h4>
      <button type="button" className="timeline-payload-button" disabled={loading}
        onClick={() => setRefresh(value => value + 1)}>Refresh email history</button>
    </div>
    <p>Workflow completion confirms queueing only. Provider acceptance does not confirm inbox delivery or reading.
      A failed submission may have partially accepted recipients; check provider tracing before retrying.</p>
    {error && <p role="alert">{error} Use Refresh email history to retry.</p>}
    {loading && <p role="status">Loading email submission history...</p>}
    {!loading && !error && !items.length && <p>No recorded email submissions linked to this notification.
      A queued request may not have reached the worker, or an older request may lack correlation data.</p>}
    <ol>{items.map(item => {
      const envelope = item.envelope;
      const succeeded = item.status === 'Succeeded';
      const localCapture = envelope?.provider === 'LocalMailpit' || item.providerReceiptId === 'local-mailpit';
      const outcome = succeeded ? localCapture ? 'Captured locally' : 'Accepted by provider'
        : item.status === 'Failed' ? envelope?.transportInvoked === false ? 'Not submitted — failed before transport' : 'Submission failed'
          : item.status;
      return <li key={`${item.requestId}:${item.attemptId || 'pending'}`}>
        <h5>{outcome}{item.attemptNumber != null ? ` · Attempt ${item.attemptNumber}` : ''}</h5>
        <time dateTime={item.createdAt}>{formatDateTime(item.createdAt)}</time>
        <dl className="notification-detail-metadata">
          <dt>Request ID</dt><dd>{item.requestId}</dd>
          {item.workflowInstanceId && <><dt>Workflow run</dt><dd>{item.workflowInstanceId}</dd></>}
          {item.workflowStepId && <><dt>Step ID</dt><dd>{item.workflowStepId}</dd></>}
          {envelope ? <>
            <dt>Provider</dt><dd>{envelope.provider}</dd>
            <dt>From</dt><dd>{envelope.from || 'Not recorded'}</dd>
            <dt>To</dt><dd>{envelope.to.join('; ') || 'None'}</dd>
            <dt>CC</dt><dd>{envelope.cc.join('; ') || 'None'}</dd>
            <dt>BCC</dt><dd>{envelope.bcc.join('; ') || 'None'}</dd>
            {(envelope.attachments?.length ?? 0) > 0 && <><dt>Attachments</dt><dd>{envelope.attachments!.map(attachment =>
              attachment.status === 'Attached'
                ? `${attachment.fileName} (${attachment.sizeBytes < 1024 ? `${attachment.sizeBytes} B` : `${(attachment.sizeBytes / 1024).toFixed(1)} KB`})`
                : `${attachment.sourceField} (skipped — no value)`).join('; ')}</dd></>}
          </> :  <><dt>Recipients</dt><dd>Recipient snapshot unavailable for this historical attempt.</dd></>}
          {item.httpStatusCode != null && <><dt>Provider HTTP status</dt><dd>{item.httpStatusCode}</dd></>}
          {item.errorClass && <><dt>Failure class</dt><dd>{item.errorClass}</dd></>}
          {item.errorCode && <><dt>Error code</dt><dd>{item.errorCode}</dd></>}
          {item.errorMessage && <><dt>Error</dt><dd>{item.errorMessage}</dd></>}
          {item.unresolvedTokens.length > 0 && <><dt>Unresolved variables</dt><dd>{item.unresolvedTokens.join(', ')}</dd></>}
          {item.providerCorrelationId && <><dt>Provider correlation ID</dt><dd>{item.providerCorrelationId}</dd></>}
          {item.providerReceiptId && <><dt>Provider receipt</dt><dd>{item.providerReceiptId}</dd></>}
          {item.completedAt && <><dt>Completed</dt><dd>{formatDateTime(item.completedAt)}</dd></>}
        </dl>
        {localCapture && <p>Local capture uses capture@localhost.test, not the configured external recipients.</p>}
      </li>;
    })}</ol>
    {nextSkip != null && <button type="button" className="btn-secondary" disabled={loading}
      onClick={() => void load(nextSkip)}>Load more email attempts</button>}
  </section>;
}
